
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { FileDown, Play, Pause, Volume2, VolumeX } from "lucide-react";
import { getAudioUrlFromData } from "./conversationUtils";

interface ConversationAudioPlayerProps {
  audioData: any;
  conversationId: string | null;
  truncateId: (id: string | undefined) => string;
}

const ConversationAudioPlayer = ({ 
  audioData, 
  conversationId,
  truncateId
}: ConversationAudioPlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrl = getAudioUrlFromData(audioData);
  
  useEffect(() => {
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      
      audio.onloadedmetadata = () => {
        setDuration(audio.duration);
      };
      
      audio.ontimeupdate = () => {
        setCurrentTime(audio.currentTime);
      };
      
      audio.onplay = () => setIsPlaying(true);
      audio.onpause = () => setIsPlaying(false);
      audio.onended = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };
      
      audioRef.current = audio;
      
      return () => {
        audio.pause();
        audio.src = "";
      };
    }
  }, [audioUrl]);
  
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);
  
  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };
  
  const handlePlayPause = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };
  
  const handleTimeChange = (newValue: number[]) => {
    if (!audioRef.current) return;
    
    const newTime = newValue[0];
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };
  
  const handleVolumeChange = (newValue: number[]) => {
    if (!audioRef.current) return;
    
    const newVolume = newValue[0];
    setVolume(newVolume);
    audioRef.current.volume = isMuted ? 0 : newVolume;
  };
  
  const toggleMute = () => {
    if (!audioRef.current) return;
    
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    audioRef.current.volume = newMuted ? 0 : volume;
  };
  
  if (!audioUrl) {
    return (
      <div className="text-center py-6">
        <p className="text-muted-foreground">No audio available for this conversation</p>
      </div>
    );
  }
  
  return (
    <Card className="overflow-hidden">
      <div className="bg-accent/50 px-6 py-3 flex items-center">
        <Pause className="h-4 w-4" />
        <h3 className="text-sm font-medium ml-2">Call Recording</h3>
      </div>
      <CardContent className="pt-6">
        <div className="flex flex-col items-center space-y-6">
          <div className="rounded-full bg-primary/10 p-6">
            <Button 
              onClick={handlePlayPause} 
              variant="outline"
              size="icon"
              className="h-16 w-16 rounded-full"
            >
              {isPlaying ? (
                <Pause className="h-8 w-8" />
              ) : (
                <Play className="h-8 w-8" />
              )}
            </Button>
          </div>
          
          <div className="w-full space-y-2">
            <div className="flex items-center space-x-2">
              <span className="text-xs text-muted-foreground w-10 text-right">
                {formatTime(currentTime)}
              </span>
              
              <Slider
                value={[currentTime]}
                max={duration || 100}
                step={0.1}
                onValueChange={handleTimeChange}
                className="flex-1"
              />
              
              <span className="text-xs text-muted-foreground w-10">
                {formatTime(duration)}
              </span>
            </div>
            
            <div className="flex items-center space-x-2 px-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={toggleMute}
              >
                {isMuted ? (
                  <VolumeX className="h-4 w-4" />
                ) : (
                  <Volume2 className="h-4 w-4" />
                )}
              </Button>
              
              <Slider
                value={[volume]}
                max={1}
                step={0.01}
                onValueChange={handleVolumeChange}
                className="w-24"
              />
              
              <div className="flex-1"></div>
              
              <Button 
                variant="outline" 
                size="sm"
                asChild
              >
                <a 
                  href={audioUrl} 
                  download={`conversation-${truncateId(conversationId)}.mp3`}
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <FileDown className="h-4 w-4 mr-2" />
                  Download
                </a>
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ConversationAudioPlayer;
